import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import * as css from './autocomplete.module.scss';
import Icon, { IconType } from '../icon';
import classNames from 'classnames';
import translations from './autocomplete.i18n';
import { useIntl } from 'react-intl';
import useClickedOutsideElement from '../../hooks/useClickedOutsideElement';
import useKeyEventOutsideElement from '../../hooks/useKeyEventOutsideElement';
import usePrevValue from '../../hooks/usePrevValue';
import { OnStedHoverCard, StedType } from '../hierarchy/onhoverhierarchyinfo';

const getSearchPartialStrings = (t: string, s: string) => {
  const text = t.toLowerCase();
  const search = s.toLowerCase();

  const index = text.indexOf(search);
  const lastChar = index + search.length;

  if (s.length === 0 || index === -1) return [t];

  return [
    t.substring(0, index),
    t.substring(index, lastChar),
    t.substring(lastChar),
  ];
};

const filterOptionsBasedOnSearch = (
  options: AutocompleteInitialDropdownOption[],
  search: string,
  inputWasFocused?: boolean,
) =>
  inputWasFocused
    ? options
    : options.filter((option) => {
        const navn = (option.customSearchString || option.navn).toLowerCase();
        const lwrCaseSearch = search.toLowerCase();

        return search === '' || navn.indexOf(lwrCaseSearch) > -1;
      });

const getNavnBasedOnSearch = (
  option: AutocompleteInitialDropdownOption,
  value: string,
) => {
  if (option.deaktivert) return <span>{option.navn}</span>;

  const partialStrings = getSearchPartialStrings(option.navn, value);

  return (
    <span>
      {partialStrings[0]}
      {partialStrings.length > 1 && (
        <>
          <span className={css.searchString}>{partialStrings[1]}</span>
          {partialStrings[2]}
        </>
      )}
    </span>
  );
};

export interface AutocompleteOption {
  navn: string;
  id: string;
  deaktivert?: boolean;
  dybde?: number;
  checked?: boolean;
  customSearchString?: string;
  onHoverType?: StedType;
}

interface AutocompleteInitialDropdownOption extends AutocompleteOption {
  type?: string;
  onClick?: () => void;
  onKeyPress?: () => void;
}

interface AutocompleteSelectDropdownOption {
  navn: JSX.Element;
  id: string;
  type?: string;
  onClick?: () => void;
  onKeyPress?: () => void;
  deaktivert?: boolean;
  dybde?: number;
  checked?: boolean;
  onHoverType?: StedType;
}

interface TextInputProps {
  value: string;
  htmlId: string;
  toggled: boolean;
  inputRef: React.RefObject<HTMLInputElement>;
  setToggle: (value: boolean) => void;
  setInputFocus: () => void;
  ariaLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  maxLength?: number;
  onChange: (value: string, toggle: boolean) => void;
  required?: boolean;
  multiple: boolean;
  multipleValueArray: AutocompleteOption[];
  clearMultipleValues: () => void;
}

const AutocompleteTextInput: React.FC<
  React.PropsWithChildren<TextInputProps>
> = ({
  value,
  htmlId,
  ariaLabel,
  setInputFocus,
  disabled,
  placeholder,
  label,
  maxLength,
  onChange,
  required,
  toggled,
  setToggle,
  inputRef,
  multiple,
  multipleValueArray,
  clearMultipleValues,
}) => {
  const [fakeHoverLabel, setFakeHoverLabel] = useState<boolean>(false);
  const [fakeHoverBorder, setFakeHoverBorder] = useState<boolean>(false);
  const [hideMultipleValues, setHideMultipleValues] = useState<boolean>(
    !multiple || multipleValueArray.length === 0,
  );
  const [multipleValuesAcc, setMultipleValuesAcc] = useState<string>();

  const { formatMessage } = useIntl();
  const defaultPlaceholder = formatMessage({
    id: translations['defaultPlaceholder'].id,
  });

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value, event.target.value !== '');
  };

  const iconDirection = toggled
    ? IconType.CloseRowTriangle
    : IconType.OpenRowTriangle;

  const getLabelStyle = () =>
    !label || label.length === 0 ? css.noLabel : css.hasLabel;

  const focusHandler = useCallback(() => {
    if (multiple) {
      setHideMultipleValues(true);
    }
  }, [multiple]);

  const blurHandler = useCallback(() => {
    if (multiple) {
      setHideMultipleValues(false);
    }
  }, [multiple]);

  useEffect(() => {
    const { current } = inputRef;

    if (current) {
      current.addEventListener('focus', focusHandler);
      current.addEventListener('blur', blurHandler);
    }

    return () => {
      if (current) {
        current.removeEventListener('focus', focusHandler);
        current.removeEventListener('blur', blurHandler);
      }
    };
  }, [inputRef, blurHandler, focusHandler]);

  useEffect(() => {
    if (multiple) {
      setMultipleValuesAcc(
        multipleValueArray.map((option) => option.navn).join(', '),
      );
    }
  }, [multiple, multipleValueArray]);

  const clear = () => {
    onChange('', true);
    if (!multiple) setInputFocus();
  };

  const getInputField = () => (
    <input
      id={htmlId}
      tabIndex={0}
      className={classNames(
        css.textInput,
        toggled ? css.clicked : '',
        getLabelStyle(),
        fakeHoverBorder ? css.fakeHoverBorder : css.textInputBorder,
      )}
      value={value}
      onChange={onChangeHandler}
      placeholder={placeholder || defaultPlaceholder}
      onFocus={(event) => event.target.select()}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-required={required}
      type={'text'}
      maxLength={maxLength}
      required={required}
      autoComplete={'off'}
      ref={inputRef}
      onKeyDown={(e) => {
        if (['ArrowUp', 'Up'].includes(e.key)) setToggle(false);
      }}
    />
  );

  return (
    <div className={css.input}>
      <div className={css.textInputContainer}>
        <span
          className={classNames(
            label ? css.textInputLabel : '',
            fakeHoverLabel ? css.fakeHoverLabel : '',
          )}
        >
          {label && (
            <label htmlFor={htmlId}>
              {label} {required && <span className={css.required}>*</span>}
            </label>
          )}
          {multiple ? (
            <span title={multipleValuesAcc}>{getInputField()}</span>
          ) : (
            getInputField()
          )}
        </span>
      </div>
      {multiple && !hideMultipleValues && (
        <div className={css.multipleValues}>{multipleValuesAcc}</div>
      )}
      <Icon
        type={IconType.Search}
        className={css.search}
        htmlId={`${htmlId}-search`}
      />
      {(value.length > 0 || (multiple && multipleValueArray.length > 0)) && (
        <div
          className={css.clear}
          onKeyPress={(e) => {
            e.stopPropagation();

            if (e.key === 'Enter') {
              clear();
              clearMultipleValues();
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            clear();
            clearMultipleValues();
          }}
          tabIndex={0}
          role={'button'}
          aria-pressed={false}
          onMouseEnter={() => {
            setFakeHoverLabel(true);
            setFakeHoverBorder(true);
          }}
          onMouseLeave={() => {
            setFakeHoverLabel(false);
            setFakeHoverBorder(false);
          }}
          onFocusCapture={() => {
            setFakeHoverBorder(true);
          }}
          onBlurCapture={() => {
            setFakeHoverBorder(false);
          }}
        >
          <Icon type={IconType.Clear} className={css.clearSvg} />
        </div>
      )}
      <div
        className={css.arrow}
        tabIndex={0}
        role={'button'}
        aria-pressed={toggled}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setInputFocus();
          } else if (e.key === 'Tab') {
            setToggle(false);
          }
        }}
        onClick={() => {
          setInputFocus();
        }}
        onMouseEnter={() => {
          setFakeHoverLabel(true);
          setFakeHoverBorder(true);
        }}
        onMouseLeave={() => {
          setFakeHoverLabel(false);
          setFakeHoverBorder(false);
        }}
        onFocusCapture={() => {
          setFakeHoverBorder(true);
        }}
        onBlurCapture={() => {
          setFakeHoverBorder(false);
        }}
      >
        <Icon type={iconDirection} className={css.arrowSvg} />
      </div>
    </div>
  );
};

const AutocompleteSelect: React.FC<
  React.PropsWithChildren<{
    option: AutocompleteSelectDropdownOption;
    renderedOption?: ReactNode;
    onChange: (
      event: React.MouseEvent | React.KeyboardEvent,
      value: AutocompleteSelectDropdownOption,
    ) => void;
    activeRef?: React.RefObject<HTMLDivElement>;
    keyDownHandler: (
      event: React.KeyboardEvent,
      option: AutocompleteSelectDropdownOption,
    ) => void;
    multiple?: boolean;
    checked?: boolean;
  }>
> = ({
  option,
  renderedOption,
  onChange,
  activeRef,
  keyDownHandler,
  multiple,
  checked,
}) => {
  const onChangeHandler = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (option.deaktivert) {
      event.stopPropagation();
      return;
    }

    if (option.id) {
      onChange(event, option);
    }
  };

  const getDybdeStyle = () => {
    switch (option.dybde) {
      case 0:
        return css.dybde0;
      case 1:
        return css.dybde1;
      case 2:
        return css.dybde2;
      case 3:
        return css.dybde3;
    }

    return css.dybdeDefault;
  };

  return (
    <div
      className={classNames(
        getDybdeStyle(),
        option.deaktivert || !option.id ? css.disabledItem : '',
      )}
      onClick={(e) => onChangeHandler(e)}
      onKeyDown={(e) => {
        keyDownHandler(e, option);
      }}
      tabIndex={-1}
      ref={activeRef}
      role={multiple ? 'option' : 'listitem'}
      aria-selected={checked}
    >
      {multiple &&
        !option.deaktivert &&
        (checked ? (
          <Icon
            type={IconType.CheckBox}
            className={option.deaktivert ? css.checkboxDisabled : css.checkbox}
          />
        ) : (
          <Icon
            type={IconType.CheckBoxOutline}
            className={
              option.deaktivert ? css.checkboxDisabled : css.checkboxOutline
            }
          />
        ))}
      {option.onHoverType !== undefined ? (
        <div className={css.dropdownItemText}>
          {renderedOption || option.navn}
        </div>
      ) : (
        <>{renderedOption || option.navn}</>
      )}
      {option.onHoverType !== undefined && (
        <div className={css.helpButton}>
          <OnStedHoverCard
            sted={option.onHoverType}
            id={option.id}
            navn={option.navn.toString()}
          />
        </div>
      )}
    </div>
  );
};

const AutocompleteDropdown: React.FC<
  React.PropsWithChildren<{
    id: string;
    value: string;
    onChange: (
      event: React.MouseEvent | React.KeyboardEvent,
      value: AutocompleteSelectDropdownOption,
    ) => void;
    setInputFocus: () => void;
    activateDropdownNavigation: (value?: boolean) => void;
    activated?: boolean;
    options?: AutocompleteInitialDropdownOption[];
    renderOption?: (option: AutocompleteSelectDropdownOption) => ReactNode;
    noOptionsText?: string;
    disabled?: boolean;
    loading?: boolean;
    multiple: boolean;
  }>
> = ({
  id,
  value,
  options,
  onChange,
  renderOption,
  activated,
  setInputFocus,
  activateDropdownNavigation,
  noOptionsText,
  disabled,
  loading,
  multiple,
}) => {
  const formatOption = (option: AutocompleteInitialDropdownOption) => ({
    ...option,
    navn: getNavnBasedOnSearch(option, value),
    deaktivert: disabled || option.deaktivert,
  });

  const [focusIndex, setFocusIndex] = useState<number>(-1);
  const selectRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const onKeyDownHandler = (
    event: React.KeyboardEvent,
    option: AutocompleteSelectDropdownOption,
  ) => {
    let tempIndex = focusIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'Down':
        while (options && tempIndex < options.length - 1) {
          tempIndex += 1;

          if (options[tempIndex].deaktivert) {
            continue;
          }

          setFocusIndex(tempIndex);
          break;
        }
        break;
      case 'ArrowUp':
      case 'Up':
        while (options && focusIndex > -1) {
          tempIndex -= 1;

          if (tempIndex === -1) {
            setFocusIndex(-1);
            setInputFocus();
            activateDropdownNavigation(false);
            break;
          }

          if (options[tempIndex].deaktivert) {
            continue;
          }

          setFocusIndex(tempIndex);
          break;
        }
        break;
      case 'Enter':
        if (option) onChange(event, option);
        if (!multiple) {
          activateDropdownNavigation(false);
        }
        setInputFocus();
        break;
      default:
        setFocusIndex(-1);
        activateDropdownNavigation(false);
        setInputFocus();
    }

    selectRef.current &&
      selectRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  let key = 0;
  const formattedOptions =
    options &&
    options.map((option) => {
      const formattedOption = formatOption(option);

      return (
        <AutocompleteSelect
          onChange={onChange}
          renderedOption={renderOption && renderOption(formattedOption)}
          option={formattedOption}
          activeRef={focusIndex === key ? selectRef : undefined}
          key={key++}
          keyDownHandler={onKeyDownHandler}
          multiple={multiple}
          checked={multiple && option.checked}
        />
      );
    });

  const setFocus = () => {
    selectRef.current && selectRef.current.focus();
  };

  React.useEffect(() => {
    if (activated && focusIndex === -1)
      setFocusIndex(
        options
          ? options.indexOf(
              options.find(
                (option) => !option.deaktivert,
              ) as AutocompleteInitialDropdownOption,
            )
          : 0,
      );
    if (activated) setFocus();
  }, [activated, focusIndex, selectRef, options]);

  const { formatMessage } = useIntl();
  const noOptionsDefaultText = formatMessage({
    id: translations['noOptions'].id,
  });
  const loadingText = formatMessage({
    id: translations['loading'].id,
  });

  const emptySelect = (
    <>{loading ? loadingText : noOptionsText || noOptionsDefaultText}</>
  );

  return (
    <div
      className={classNames(css.dropdownmenu, css.dropdownBorder)}
      ref={dropdownRef}
      id={id}
      role={'list'}
    >
      {!loading &&
      options &&
      formattedOptions &&
      formattedOptions.length > 0 ? (
        formattedOptions
      ) : (
        <AutocompleteSelect
          onChange={onChange}
          option={{ id: 'empty', navn: emptySelect, deaktivert: true }}
          keyDownHandler={() => {}}
        />
      )}
    </div>
  );
};

interface AutocompleteProps {
  id: string;
  onChange: (event: object, value: any) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  renderOption?: (option: any) => React.ReactNode; // eslint-disable-line @typescript-eslint/no-explicit-any
  options?: AutocompleteOption[];
  loading?: boolean;
  disabled?: boolean;
  noOptionsText?: string;
  placeholder?: string;
  searchThreshold?: number;
  required?: boolean;
  defaultValue?: AutocompleteOption;
  containerClassname?: string;
}

interface AutocompleteWithAriaLabel extends AutocompleteProps {
  ariaLabel: string;
  label?: never;
}

interface AutocompleteWithLabel extends AutocompleteProps {
  label: string;
  ariaLabel?: never;
}

interface SelectedOption {
  selectedOption?: AutocompleteOption;
  selectedOptions?: never;
  multiple?: false;
}

interface SelectedOptions {
  selectedOptions?: AutocompleteOption[];
  selectedOption?: never;
  multiple?: true;
}

type Options = SelectedOption | SelectedOptions;

type AutocompleteType = (AutocompleteWithAriaLabel | AutocompleteWithLabel) &
  Options;

const Autocomplete: React.FC<React.PropsWithChildren<AutocompleteType>> = ({
  id,
  onChange,
  label,
  ariaLabel,
  searchThreshold = 1,
  options,
  loading,
  disabled,
  noOptionsText,
  renderOption,
  placeholder,
  required,
  defaultValue,
  selectedOption,
  multiple,
  selectedOptions,
  containerClassname,
}) => {
  if (
    options &&
    new Set(options.map((option) => option.id)).size < options.length
  ) {
    console.error(
      'The id field must be unique for all options supplied to the Autocomplete component',
    );
  }

  if (multiple && options) {
    options = options.map((option) => ({
      ...option,
      checked: option.checked !== undefined ? option.checked : false,
    }));
  }
  const [isToggled, setToggle] = useState<boolean>(false);
  const [value, setValue] = useState<string>(
    defaultValue ? defaultValue.navn : '',
  );

  const [multipleValueArray, setMultipleValueArray] = useState<
    AutocompleteOption[]
  >(options ? options.filter((option) => option.checked) : []);

  const [dropdownNavigationActivated, setDropdownNavigationActivated] =
    useState<boolean>(false);
  const [inputWasFocused, setInputWasFocused] = useState<boolean>(false);

  const isValidInput = useCallback(
    (nextValue: string) =>
      nextValue !== undefined &&
      nextValue !== '' &&
      options &&
      (options.find(
        (option) => option.navn === nextValue,
      ) as AutocompleteInitialDropdownOption),
    [options],
  );

  const toggle = useCallback(
    (down: boolean) => {
      if (down && !isToggled) setInputWasFocused(true);
      setToggle(down);
    },
    [setToggle, setInputWasFocused, isToggled],
  );

  const activateDropdownNavigation = (value: boolean = true) => {
    setDropdownNavigationActivated(value);
  };

  const textFieldOnChangeHandler = (value: string, toggleValue: boolean) => {
    toggle(toggleValue);
    setValue(value);

    const validInput = isValidInput(value);

    if (!multiple) {
      if (!validInput) {
        onChange({}, undefined);
      } else {
        onChange({}, validInput);
      }
    }
  };

  const dropdownOnChangeHandler = (
    event: React.MouseEvent | React.KeyboardEvent,
    option: AutocompleteSelectDropdownOption,
  ) => {
    const element =
      options && options.find((element) => option.id === element.id);

    if (multiple && element) {
      const tempArray = multipleValueArray.slice(0);
      const tempElement = tempArray.find((val) => val.id === element.id);
      const index = tempElement ? tempArray.indexOf(tempElement) : -1;

      if (index === -1) {
        tempArray.push({ ...element, checked: true });
      } else {
        tempArray.splice(index, 1);
      }

      setMultipleValueArray(tempArray);
      onChange(event, tempArray);
    } else {
      setValue(element ? element.navn : '');
      element && element.navn && onChange(event, element);
    }

    event.stopPropagation();
    if (!multiple) toggle(!isToggled);
  };

  const clearMultipleValues = () => {
    if (multiple) {
      setMultipleValueArray([]);
      onChange({}, []);
    }
  };

  const handleOnFocusOut = useCallback(() => {
    if (!multiple && !isValidInput(value)) {
      setValue('');
      onChange({}, undefined);
    }

    toggle(false);
  }, [isValidInput, value, setValue, onChange, toggle, multiple]);

  const autocompleteRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const clickedOutside = useClickedOutsideElement(autocompleteRef);
  const keyEventOutside = useKeyEventOutsideElement(autocompleteRef);
  const prevClickedOutside = usePrevValue(clickedOutside);
  const prevKeyEventOutside = usePrevValue(keyEventOutside);

  useEffect(() => {
    if (clickedOutside && clickedOutside !== prevClickedOutside) {
      handleOnFocusOut();
    }
  }, [clickedOutside, handleOnFocusOut, prevClickedOutside]);

  useEffect(() => {
    if (keyEventOutside && keyEventOutside !== prevKeyEventOutside) {
      handleOnFocusOut();
    }
  }, [keyEventOutside, handleOnFocusOut, prevKeyEventOutside]);

  useEffect(() => {
    if (selectedOption && selectedOption.navn !== value)
      setValue(selectedOption.navn);

    if (selectedOptions) {
      const checkedOptions = selectedOptions.filter((option) => option.checked);

      if (checkedOptions !== multipleValueArray) {
        setMultipleValueArray(selectedOptions);
      }
    }
  }, [selectedOption, selectedOptions, value, multipleValueArray]);

  const setInputFocus = () => {
    inputRef.current && inputRef.current.focus();
  };

  const formatIfMultipleOptions = (filteredOptions: AutocompleteOption[]) =>
    multiple
      ? filteredOptions.map((option) => ({
          ...option,
          checked:
            option.checked !== undefined
              ? multipleValueArray.find(
                  (element) => element.id === option.id,
                ) !== undefined
              : undefined,
        }))
      : filteredOptions;

  return (
    <div
      id={id}
      className={classNames(css.autocomplete, containerClassname)}
      onKeyDown={(e) => {
        if (['Enter', 'Space'].includes(e.key)) toggle(!isToggled);
        else if (['Escape', 'Esc'].includes(e.key)) toggle(false);
        else if (['ArrowDown', 'Down'].includes(e.key)) {
          activateDropdownNavigation();
          toggle(true);
        }
      }}
      onClick={() => toggle(!isToggled)}
      ref={autocompleteRef}
      role={'combobox'}
      aria-expanded={isToggled}
      aria-controls={`${id}-autocomplete-input ${id}-autocomplete-dropdown`}
      onChange={() => setInputWasFocused(false)}
    >
      <AutocompleteTextInput
        value={value}
        placeholder={placeholder}
        onChange={textFieldOnChangeHandler}
        label={label}
        htmlId={`${id}-autocomplete-input`}
        ariaLabel={ariaLabel}
        toggled={isToggled}
        setToggle={toggle}
        inputRef={inputRef}
        setInputFocus={setInputFocus}
        disabled={disabled}
        required={required}
        multiple={multiple!!}
        multipleValueArray={multipleValueArray}
        clearMultipleValues={clearMultipleValues}
      />
      {isToggled && (
        <AutocompleteDropdown
          id={`${id}-autocomplete-dropdown`}
          value={value}
          options={
            options &&
            formatIfMultipleOptions(
              value.length >= searchThreshold
                ? filterOptionsBasedOnSearch(options, value, inputWasFocused)
                : options,
            )
          }
          renderOption={renderOption}
          onChange={dropdownOnChangeHandler}
          activated={dropdownNavigationActivated}
          activateDropdownNavigation={activateDropdownNavigation}
          setInputFocus={setInputFocus}
          noOptionsText={noOptionsText}
          disabled={disabled}
          loading={loading}
          multiple={multiple!!}
        />
      )}
    </div>
  );
};

export default Autocomplete;
